/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

export interface CatalogPublication {
  property_id: string;
  portal: string;
  publication_id: string;
  published_at: string;
  property_title: string | null;
  property_image: string | null;
  status: string | null;
  permalink?: string;
  ad_id?: string;
  /** On a post's entry: how its Lead Ad ended ("created" / "failed") and, if failed, why. */
  lead_ad_status?: 'created' | 'failed';
  lead_ad_error?: string;
  /** On a post's entry: the Lead Ad created for it, and when it was paused (if it was). */
  lead_ad_id?: string;
  lead_ad_paused_at?: string;
  /** On a boost or Lead Ad entry: when it was paused (if it was). */
  paused_at?: string;
  /** Only on `<portal>_boost` entries: the post that was boosted. */
  boosted_post_id?: string;
  daily_budget_cents?: number;
  duration_days?: number;
}

export interface PublicationsCatalogResponse {
  entries: CatalogPublication[];
}

export type PortalGroup = {
  portal: string;
  label: string;
  entries: CatalogPublication[];
};

export const PORTAL_I18N_KEYS: Record<string, string> = {
  olx: 'publications.portals.olx',
  facebook_feed: 'publications.portals.facebook_feed',
  facebook_marketplace: 'publications.portals.facebook_marketplace',
  facebook_lead_ad: 'publications.portals.facebook_lead_ad',
  zap: 'publications.portals.zap',
  vivareal: 'publications.portals.vivareal',
  chavesnamao: 'publications.portals.chavesnamao',
  instagram: 'publications.portals.instagram',
};

const SOCIAL_CATALOG_PORTALS = new Set(['facebook_feed', 'facebook_marketplace', 'facebook_lead_ad', 'instagram']);

/**
 * Whether a catalog portal identifier is a social network (Facebook/Instagram
 * surface) rather than a listing portal (OLX, ZAP, etc.).
 *
 * @param portal - Portal identifier, as found on {@link CatalogPublication.portal}.
 * @returns True for a Facebook/Instagram surface.
 */
export function isSocialPortal(portal: string): boolean {
  return SOCIAL_CATALOG_PORTALS.has(portal);
}

/**
 * Splits portal groups into the two sections of the catalog page: social
 * networks (Facebook/Instagram surfaces) and listing portals (OLX, ZAP, ...).
 * Each section keeps the order of the groups it receives.
 *
 * @param groups - Portal groups, as returned by {@link groupByPortal}.
 * @returns The social groups and the listing-portal groups.
 */
export function splitGroupsBySection(groups: PortalGroup[]): { social: PortalGroup[]; portals: PortalGroup[] } {
  return {
    social: groups.filter((group) => isSocialPortal(group.portal)),
    portals: groups.filter((group) => !isSocialPortal(group.portal)),
  };
}

/**
 * Whether a catalog entry is a boost (`facebook_boost` / `instagram_boost`).
 * Boosts describe an ad running on a post, not a publication of their own, so
 * they are shown on the post's row instead of being listed as publications.
 *
 * @param portal - Portal identifier, as found on {@link CatalogPublication.portal}.
 * @returns True for a boost entry.
 */
export function isBoostPortal(portal: string): boolean {
  return portal.endsWith('_boost');
}

/**
 * Whether a catalog entry is the Lead Ad created with a post
 * (`facebook_lead_ad`). Like a boost, it is an ad running on a post rather
 * than a publication of its own, so it is shown on its post's card instead of
 * in a lane of its own (see {@link withLeadAdState}).
 *
 * @param portal - Portal identifier, as found on {@link CatalogPublication.portal}.
 * @returns True for a Lead Ad entry.
 */
export function isLeadAdPortal(portal: string): boolean {
  return portal === 'facebook_lead_ad';
}

/** How a post's Lead Ad is doing, as its card shows it. */
export type LeadAdBadgeState = 'active' | 'paused';

/**
 * The Lead Ad badge of a post's card. Only a post that has a Lead Ad gets one:
 * a post whose Lead Ad was never requested, is still being created (the live
 * publish status reports that) or could not be created (the property's
 * Publicação tab explains why) shows no badge at all.
 *
 * @param entry - A catalog publication, after {@link withLeadAdState}.
 * @returns The badge to show, if any.
 */
export function leadAdBadgeState(entry: CatalogPublication): LeadAdBadgeState | null {
  if (entry.lead_ad_status !== 'created') return null;
  return entry.lead_ad_paused_at ? 'paused' : 'active';
}

/**
 * Attaches each Lead Ad to the post it was created with. Newer posts carry
 * their own Lead Ad outcome; for older ones the ad is matched by the time of
 * the publish, since a publish writes its post and its Lead Ad with the same
 * `published_at`. The Lead Ad entries themselves are then left out of the
 * lanes by {@link groupByPortal}.
 *
 * @param entries - Flat list of publications.
 * @returns The list with Lead Ad state filled in on the posts that have one.
 */
export function withLeadAdState(entries: CatalogPublication[]): CatalogPublication[] {
  const adByPublish = new Map<string, CatalogPublication>();
  for (const entry of entries) {
    if (isLeadAdPortal(entry.portal) && !entry.publication_id.startsWith('pending:')) {
      adByPublish.set(`${entry.property_id}|${entry.published_at}`, entry);
    }
  }
  return entries.map((entry) => {
    if (entry.portal !== 'facebook_feed' || entry.lead_ad_status) return entry;
    const ad = adByPublish.get(`${entry.property_id}|${entry.published_at}`);
    if (!ad) return entry;
    return {
      ...entry,
      lead_ad_status: 'created',
      lead_ad_id: ad.publication_id,
      ...(ad.paused_at ? { lead_ad_paused_at: ad.paused_at } : {}),
    };
  });
}

/**
 * Resolves the i18n key for a portal label, falling back to a derived key when
 * the portal is not in the known map.
 *
 * @param portal - Portal identifier.
 * @returns The i18n translation key.
 */
export function getPortalI18nKey(portal: string): string {
  return PORTAL_I18N_KEYS[portal] ?? `publications.portals.${portal}`;
}

/**
 * Produces an uppercase display label for a portal identifier.
 *
 * @param portal - Portal identifier.
 * @returns The uppercased label.
 */
export function getPortalLabel(portal: string): string {
  return portal.toUpperCase();
}

/**
 * Groups catalog publications by their portal into ordered portal groups.
 * Errored publications are dropped -- this catalog is a showcase of live
 * posts, not a queue/retry surface (that's the per-property "Publicação"
 * tab, which already surfaces errors with a retry action). Boosts and Lead
 * Ads are not publications of their own: a Lead Ad is shown on its post's
 * card.
 *
 * @param entries - Flat list of publications.
 * @returns One group per portal, each with its label and entries.
 */
export function groupByPortal(entries: CatalogPublication[]): PortalGroup[] {
  const map = new Map<string, CatalogPublication[]>();
  for (const entry of withLeadAdState(entries)) {
    if (entry.status === 'error' || isBoostPortal(entry.portal) || isLeadAdPortal(entry.portal)) continue;
    const existing = map.get(entry.portal) ?? [];
    existing.push(entry);
    map.set(entry.portal, existing);
  }
  return Array.from(map.entries()).map(([portal, portalEntries]) => ({
    portal,
    label: getPortalLabel(portal),
    entries: portalEntries,
  }));
}
